import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface Project {
  id: string;
  slug: string;
  name: string;
  ownerUserId: string;
  status: 'active' | 'paused' | 'archived';
  monthlyCostCeilingUsd: number;
  brief: string | null;
  logoUrl: string | null;
  /** When true (default), the uploaded logo image is treated as a
   *  lockup with the brand name already in it — slide + reel templates
   *  render the image without a separate wordmark. */
  logoIncludesWordmark: boolean;
  publicUrl: string | null;
  channels: string[];
  bannedPhrases: string[];
  voiceRules: string[];
  antiConventions: string | null;
  inconvenientTruths: string | null;
  helpfulnessRatioUtility: number;
  helpfulnessRatioPromo: number;
  createdAt: string;
  updatedAt: string;
  role: 'owner' | 'member';
}

export interface BriefPatch {
  name?: string;
  brief?: string | null;
  logoUrl?: string | null;
  logoIncludesWordmark?: boolean;
  publicUrl?: string | null;
  channels?: ('linkedin' | 'instagram')[];
  bannedPhrases?: string[];
  voiceRules?: string[];
  antiConventions?: string | null;
  inconvenientTruths?: string | null;
  helpfulnessRatioUtility?: number;
  helpfulnessRatioPromo?: number;
}

export function useProjects() {
  return useQuery({
    queryKey: ['projects'],
    queryFn: () => api<{ projects: Project[] }>('/api/projects').then((r) => r.projects),
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api<{ project: Project }>('/api/projects', {
        method: 'POST',
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function useUpdateBrief(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: BriefPatch) =>
      api<{ project: Project }>(`/api/projects/${projectId}/brief`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectId] });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

// ───────── Members ─────────

export interface ProjectMember {
  userId: string;
  email: string;
  role: 'owner' | 'member';
  createdAt: string;
}

export function useProjectMembers(projectId: string | undefined) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ['projects', projectId, 'members'],
    queryFn: () => api<{ members: ProjectMember[] }>(`/api/projects/${projectId}/members`).then((r) => r.members),
  });
}

export function useInviteMember(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; role: 'owner' | 'member' }) =>
      api<{ member: ProjectMember; userCreated: boolean }>(`/api/projects/${projectId}/members`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
    },
  });
}

export function useRemoveMember(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<{ ok: boolean }>(`/api/projects/${projectId}/members/${userId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'members'] });
    },
  });
}
